import type {
  ParsedTemplateVariable,
  TemplateVariableConstraint,
} from './template-variable.interface'
import {
  extractFunctionName,
  extractParenValue,
  tokenizeVariableContent,
} from './template-variable-tokenizer.util'
import type { TemplateVariableValidatorType } from './template-variable-validator.type'

const VARIABLE_BLOCK_REGEX = /\{([^{}]+)\}/g

const TYPE_VALIDATOR_SET = new Set<string>(['text', 'number', 'url', 'email', 'enum'])

const CONSTRAINT_NAME_SET = new Set<string>(['min', 'max', 'minLength', 'maxLength', 'regex'])

interface VariableAccumulator {
  type: TemplateVariableValidatorType
  enumOptionList: string[]
  label: string | null
  helper: string | null
  defaultValue: string | null
  constraintList: TemplateVariableConstraint[]
}

const createAccumulator = (): VariableAccumulator => ({
  type: 'text',
  enumOptionList: [],
  label: null,
  helper: null,
  defaultValue: null,
  constraintList: [],
})

const parseEnumOptions = (token: string): string[] => {
  const value = extractParenValue(token)
  if (!value) return []
  return value
    .split(',')
    .map((opt) => opt.trim())
    .filter(Boolean)
}

const processTypeToken = (acc: VariableAccumulator, token: string, fnName: string): boolean => {
  if (!TYPE_VALIDATOR_SET.has(fnName)) return false

  acc.type = fnName as TemplateVariableValidatorType
  if (fnName === 'enum') {
    acc.enumOptionList = parseEnumOptions(token)
  }
  return true
}

const processConstraintToken = (
  acc: VariableAccumulator,
  token: string,
  fnName: string
): boolean => {
  if (!CONSTRAINT_NAME_SET.has(fnName)) return false

  acc.constraintList.push({ name: fnName, value: extractParenValue(token) ?? '' })
  return true
}

const MODIFIER_HANDLERS: Record<string, (acc: VariableAccumulator, token: string) => void> = {
  label: (acc, token) => {
    acc.label = extractParenValue(token)
  },
  helper: (acc, token) => {
    acc.helper = extractParenValue(token)
  },
  default: (acc, token) => {
    acc.defaultValue = extractParenValue(token)
  },
}

const processToken = (acc: VariableAccumulator, token: string): void => {
  const fnName = extractFunctionName(token)

  if (processTypeToken(acc, token, fnName)) return
  if (processConstraintToken(acc, token, fnName)) return

  MODIFIER_HANDLERS[fnName]?.(acc, token)
}

const parseSingleVariable = (inner: string): ParsedTemplateVariable | null => {
  const tokenList = tokenizeVariableContent(inner)
  if (tokenList.length === 0) return null

  const name = tokenList[0]
  if (!name) return null

  const acc = createAccumulator()
  for (let i = 1; i < tokenList.length; i++) {
    processToken(acc, tokenList[i])
  }

  return { name, ...acc }
}

/**
 * Parses all template variables from a template content string.
 * Variables follow the syntax: {variableName:type:constraint(value):modifier(value)}
 */
export const parseTemplateVariables = (content: string): ParsedTemplateVariable[] => {
  const resultList: ParsedTemplateVariable[] = []
  let match: RegExpExecArray | null = VARIABLE_BLOCK_REGEX.exec(content)

  while (match !== null) {
    const parsed = parseSingleVariable(match[1])
    if (parsed) {
      resultList.push(parsed)
    }
    match = VARIABLE_BLOCK_REGEX.exec(content)
  }

  return resultList
}
